import { useEffect, useRef, useState } from "react"

import { message } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { toDateQuery } from "../lib/dates"

export interface IHistoryPageQuery {
  fromDate: string
  toDate: string
  pageNo: number
  perPage: number
}

interface IPagedHistoryResponse<T> {
  data: T[]
  totalRecords: number
}

type FetchHistoryPage<T> = (query: IHistoryPageQuery) => Promise<IPagedHistoryResponse<T>>

export const usePagedHistory = <T>(fetchPage: FetchHistoryPage<T>, filters: unknown[]) => {
  const { dateRange, countryCode } = useTypedSelector((state) => state.filter)
  const [rows, setRows] = useState<T[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageNo, setPageNo] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const requestIdRef = useRef(0)

  const loadPage = async () => {
    if (!dateRange.from || !dateRange.to) return

    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      const response = await fetchPage({ ...toDateQuery(dateRange), pageNo, perPage })
      if (requestId !== requestIdRef.current) return
      setRows(response.data)
      setTotalRecords(response.totalRecords)
    } catch {
      if (requestId !== requestIdRef.current) return
      message.error("Something went wrong")
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    setPageNo(1)
  }, [dateRange, countryCode, ...filters])

  useEffect(() => {
    loadPage()
    return () => {
      requestIdRef.current++
    }
  }, [dateRange, countryCode, pageNo, perPage, reloadKey, ...filters])

  const reload = () => setReloadKey((key) => key + 1)

  const changePage = (nextPageNo: number, nextPerPage: number) => {
    setPageNo(nextPerPage === perPage ? nextPageNo : 1)
    setPerPage(nextPerPage)
  }

  return { rows, totalRecords, loading, pageNo, perPage, changePage, reload }
}
